/* eslint-disable no-unused-vars */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { toast } from 'react-toastify';

/** Defaults align with DJR AppMaintenance/status; manager pair matches legacy Time Manager cutoff. */
export const DEFAULT_LOCKOUT_SETTINGS = {
  podLockEditDays: 5,
  timeLockoutWeekday: 2,
  timeLockoutHour: 13,
  managerTimeLockoutWeekday: 2,
  managerTimeLockoutHour: 20
};

let runtimeLockout = { ...DEFAULT_LOCKOUT_SETTINGS };

const clampInt = (value, min, max, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
};

const computeMinTimeDate = (weekdayCutoff, hourCutoff) => {
  const today = dayjs().format('YYYY-MM-DD HH:mm');
  const day = dayjs(today).day();
  const hour = dayjs(today).hour();
  if (!(day >= weekdayCutoff && hour >= hourCutoff)) {
    return dayjs(today)
      .subtract(7 + (day - 1), 'days')
      .format('YYYY-MM-DD');
  }
  return dayjs(today)
    .subtract(day === 0 ? 6 : day - 1, 'days')
    .format('YYYY-MM-DD');
};

/**
 * Sync lockout rules from Firestore AppMaintenance/status (shared with DJR).
 * @param {Record<string, unknown>|null|undefined} data
 * @returns {typeof DEFAULT_LOCKOUT_SETTINGS}
 */
export const applyLockoutSettingsFromRemote = (data) => {
  if (!data || typeof data !== 'object') {
    runtimeLockout = { ...DEFAULT_LOCKOUT_SETTINGS };
    return runtimeLockout;
  }
  runtimeLockout = {
    podLockEditDays: clampInt(
      data.podLockEditDays,
      0,
      365,
      DEFAULT_LOCKOUT_SETTINGS.podLockEditDays
    ),
    timeLockoutWeekday: clampInt(
      data.timeLockoutWeekday,
      0,
      6,
      DEFAULT_LOCKOUT_SETTINGS.timeLockoutWeekday
    ),
    timeLockoutHour: clampInt(
      data.timeLockoutHour,
      0,
      23,
      DEFAULT_LOCKOUT_SETTINGS.timeLockoutHour
    ),
    managerTimeLockoutWeekday: clampInt(
      data.managerTimeLockoutWeekday,
      0,
      6,
      DEFAULT_LOCKOUT_SETTINGS.managerTimeLockoutWeekday
    ),
    managerTimeLockoutHour: clampInt(
      data.managerTimeLockoutHour,
      0,
      23,
      DEFAULT_LOCKOUT_SETTINGS.managerTimeLockoutHour
    )
  };
  return runtimeLockout;
};

export const getLockoutSettings = () => ({ ...runtimeLockout });

const checkPOD = (user, record) => {
  if (user && user.admin) return false;
  const { podLockEditDays } = runtimeLockout;
  const lastDayToEdit = dayjs()
    .subtract(podLockEditDays, 'day')
    .format('YYYY-MM-DD');
  if (lastDayToEdit >= record.data.Date && record.data.Date !== '') {
    toast.info(
      `I'm sorry, you can no longer add/modify Tasks for "${record.data.Date}".`
    );
    return true;
  }
  return false;
};

const checkTime = (user, CurrentDate, returnForcedCutoff) => {
  if (returnForcedCutoff) {
    return undefined;
  }
  const useManagerLockout = Boolean(user && user.manager && !user.admin);
  const weekdayCutoff = useManagerLockout
    ? runtimeLockout.managerTimeLockoutWeekday
    : runtimeLockout.timeLockoutWeekday;
  const hourCutoff = useManagerLockout
    ? runtimeLockout.managerTimeLockoutHour
    : runtimeLockout.timeLockoutHour;
  const minTimeDate = computeMinTimeDate(weekdayCutoff, hourCutoff);
  const dateDiff = dayjs(CurrentDate.value).diff(minTimeDate);
  const dayDiff = Math.ceil(dateDiff / (1000 * 3600 * 24));

  if (dayDiff < 0) {
    if (!toast.isActive('timeLockout')) {
      toast.error('You can no longer edit time for this date.', {
        toastId: 'timeLockout'
      });
    }
    return true;
  }
  return false;
};

export { checkPOD, checkTime };
